import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { walkNotes } from "../../components/JobEstimates";
import { Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import {
  paths,
  computeTotals,
  depositCents,
  type Deposit,
  type Estimate,
  type Invoice,
  type LineItem,
  type PricebookItem,
} from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { CustomerPicker } from "../../components/CustomerPicker";
import { DepositInput } from "../../components/SalesSettingsPanel";
import { textareaClass } from "../../components/JobEditCard";
import { useSalesSettings, useTeamMembers } from "../../hooks/useSalesSettings";

const money = (c: number) => `$${(c / 100).toFixed(2)}`;
// Amount cell dims when the line's price is hidden on customer documents.
const cnAmt = (v: "show" | "hide") =>
  v === "hide" ? "pb-1.5 text-right text-steel/50" : "pb-1.5 text-right text-ink";

interface EditableLine extends LineItem {
  key: string;
}

function blankLine(key: string): EditableLine {
  return {
    key,
    description: "",
    quantity: 1,
    unitPriceCents: 0,
    unitCostCents: null,
    taxable: true,
    itemId: null,
    priceVisibility: "show",
    components: null,
    showComponents: true,
  };
}

export function SalesDocEditor({
  kind,
  existing,
  forCustomer,
  onDone,
}: {
  kind: "estimate" | "invoice";
  existing: ((Estimate | Invoice) & { id: string }) | null;
  /** A new one made from a customer's page starts with that customer. */
  forCustomer?: { id: string; name: string };
  onDone: () => void;
}) {
  const { tenantId, user } = useAuth();
  const settings = useSalesSettings(tenantId);
  const team = useTeamMembers(tenantId);
  const est = kind === "estimate" ? (existing as (Estimate & { id: string }) | null) : null;
  const [typeId, setTypeId] = useState(est?.typeId ?? "");
  // A new estimate is its maker's sale; an invoice names its owner only when someone picks one.
  const [salespersonUid, setSalespersonUid] = useState(existing ? (existing.salespersonUid ?? "") : kind === "estimate" ? (user?.uid ?? "") : "");
  // Clevis staff looking in ("Log in as") aren't on the team: no owner by default.
  useEffect(() => {
    if (!existing && team.length && salespersonUid && !team.some((m) => m.uid === salespersonUid)) setSalespersonUid("");
  }, [team, existing, salespersonUid]);
  const [deposit, setDeposit] = useState<Deposit | null>(est?.deposit ?? null);
  const [terms, setTerms] = useState(existing?.terms ?? "");
  const [termsTouched, setTermsTouched] = useState(!!existing);
  const [showTerms, setShowTerms] = useState(false);
  const [pricebook, setPricebook] = useState<Array<PricebookItem & { id: string }>>([]);
  const [customerId, setCustomerId] = useState(existing?.customerId ?? forCustomer?.id ?? "");
  const [customerName, setCustomerName] = useState(existing?.customerName ?? forCustomer?.name ?? "");
  const [taxRate, setTaxRate] = useState(existing ? (existing.taxRateBps / 100).toString() : "0");
  const [notes, setNotes] = useState(existing?.notes ?? "");
  // The customer's job walks (what the tech found), offered as a starting point for the notes.
  const [walks, setWalks] = useState<Array<{ jobId: string; title: string; doneAt: number; findings: string; notes: string[] }>>([]);
  useEffect(() => {
    if (!tenantId || !customerId) {
      setWalks([]);
      return;
    }
    let dead = false;
    void getDocs(query(collection(db, paths.tenantJobs(tenantId)), where("customerId", "==", customerId)))
      .then((snap) => {
        if (dead) return;
        setWalks(
          snap.docs
            .map((d) => ({ title: (d.get("title") as string) ?? "", walk: d.get("jobWalk") as { jobId: string; doneAt: number; findings: string; notes: string[] } | undefined }))
            .filter((x): x is { title: string; walk: { jobId: string; doneAt: number; findings: string; notes: string[] } } => !!x.walk)
            .map((x) => ({ jobId: x.walk.jobId, title: x.title, doneAt: x.walk.doneAt, findings: x.walk.findings, notes: x.walk.notes ?? [] }))
            .sort((a, b) => b.doneAt - a.doneAt)
            .slice(0, 5),
        );
      })
      .catch(() => undefined);
    return () => {
      dead = true;
    };
  }, [tenantId, customerId]);
  // The customer's maintenance-plan discount: the server keeps its line in step; here it's a row under the lines.
  const [mdMode, setMdMode] = useState<"auto" | "off">(existing?.memberDiscount?.mode ?? "auto");
  const [planDiscount, setPlanDiscount] = useState<{ name: string; bps: number } | null>(existing?.memberDiscount ? { name: existing.memberDiscount.name, bps: existing.memberDiscount.bps } : null);
  useEffect(() => {
    if (!tenantId || !customerId) {
      setPlanDiscount(null);
      return;
    }
    let dead = false;
    getDocs(query(collection(db, paths.tenantServiceAgreements(tenantId)), where("customerId", "==", customerId)))
      .then((snap) => {
        if (dead) return;
        let best: { name: string; bps: number } | null = null;
        for (const d of snap.docs) {
          const bps = (d.get("discountBps") as number | undefined) ?? 0;
          if (d.get("status") === "active" && bps > 0 && (!best || bps > best.bps)) best = { name: (d.get("name") as string) ?? "Member", bps };
        }
        setPlanDiscount(best);
      })
      .catch(() => undefined);
    return () => {
      dead = true;
    };
  }, [tenantId, customerId]);
  const [lines, setLines] = useState<EditableLine[]>(
    existing?.lines.filter((l) => !l.memberDiscount).map((l, i) => ({
      ...l,
      key: `e${i}`,
      // Docs written before kits/visibility existed lack these fields.
      priceVisibility: l.priceVisibility ?? "show",
      components: l.components ?? null,
      showComponents: l.showComponents ?? true,
    })) ?? [blankLine("n0")],
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(query(collection(db, paths.tenantPricebook(tenantId)), orderBy("name")), (snap) =>
        setPricebook(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as PricebookItem) }))
            .filter((p) => !p.archivedAt),
        ),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  // A new invoice starts with the company's invoice terms (once they load).
  useEffect(() => {
    if (kind === "invoice" && !termsTouched && settings.invoiceTerms) setTerms(settings.invoiceTerms);
  }, [kind, termsTouched, settings.invoiceTerms]);

  function pickType(id: string) {
    setTypeId(id);
    const t = settings.estimateTypes.find((x) => x.id === id);
    // The type's terms and deposit come along (still editable for this estimate).
    if (t) {
      setTerms(t.terms);
      setDeposit(t.deposit);
      setTermsTouched(true);
    }
  }

  const discountCents = useMemo(() => {
    if (!planDiscount || mdMode !== "auto") return 0;
    const base = lines.filter((l) => l.description.trim() !== "").reduce((s, l) => s + Math.round((Number(l.quantity) || 0) * l.unitPriceCents), 0);
    return Math.max(0, Math.round((base * planDiscount.bps) / 10000));
  }, [lines, planDiscount, mdMode]);
  const totals = useMemo(() => {
    const valid = lines.filter((l) => l.description.trim() !== "");
    const withDiscount = discountCents > 0 ? [...valid, { ...blankLine("md"), description: "Member discount", quantity: 1, unitPriceCents: -discountCents, taxable: valid.some((l) => l.taxable) }] : valid;
    return computeTotals(withDiscount, Math.round(parseFloat(taxRate || "0") * 100));
  }, [lines, taxRate, discountCents]);

  function setLine(key: string, patch: Partial<LineItem>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function addFromPricebook(item: PricebookItem & { id: string }) {
    // A kit snapshots its components onto the line: inventory pulls them on
    // invoice send, and (optionally) they print on the customer doc, unpriced.
    const isKit = item.kind === "kit" && (item.components ?? []).length > 0;
    setLines((prev) => [
      ...prev.filter((l) => l.description.trim() !== "" || prev.length === 1),
      {
        key: `p${item.id}${Date.now()}`,
        description: item.name + (item.description ? ` — ${item.description}` : ""),
        quantity: 1,
        unitPriceCents: item.priceCents,
        unitCostCents: item.costCents,
        taxable: item.taxable,
        itemId: item.id,
        priceVisibility: "show",
        components: isKit
          ? item.components.map((c) => ({ itemId: c.itemId, name: c.name, quantity: c.quantity }))
          : null,
        showComponents: isKit ? item.showComponents !== false : true,
        ...(item.photoUrl ? { photoUrl: item.photoUrl } : {}),
      },
    ]);
    setPickerOpen(false);
    setPickerSearch("");
  }

  async function save() {
    if (!tenantId) return;
    const valid = lines
      .filter((l) => l.description.trim() !== "")
      .map(({ key: _key, ...l }) => ({ ...l, quantity: Number(l.quantity) || 1 }));
    if (valid.length === 0) {
      setError("Add at least one line item.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = {
        tenantId,
        customerId: customerId || null,
        lines: valid,
        taxRateBps: Math.round(parseFloat(taxRate || "0") * 100),
        notes,
        terms,
        salespersonUid: salespersonUid || null,
        memberDiscountMode: mdMode,
        ...(kind === "estimate" ? { typeId: typeId || null, deposit } : {}),
      };
      if (existing) {
        await call("updateSalesDoc", { ...payload, docId: existing.id, kind });
      } else {
        await call(kind === "estimate" ? "createEstimate" : "createInvoice", payload);
      }
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const filteredPricebook = pricebook.filter(
    (p) =>
      !pickerSearch ||
      p.name.toLowerCase().includes(pickerSearch.toLowerCase()) ||
      p.category.toLowerCase().includes(pickerSearch.toLowerCase()),
  );

  return (
    <Card>
      <CardTitle>
        {existing ? `Edit ${kind} #${existing.number}` : `New ${kind}`}
      </CardTitle>
      <div className="mb-3 grid grid-cols-2 gap-3">
        <div>
          <Label>Customer</Label>
          {tenantId ? (
            <CustomerPicker
              tenantId={tenantId}
              value={customerId}
              valueName={customerName}
              onChange={(c) => {
                setCustomerId(c?.id ?? "");
                setCustomerName(c?.displayName ?? "");
              }}
            />
          ) : null}
        </div>
        <div>
          <Label>Tax rate %</Label>
          <Input type="number" step="0.01" min="0" max="50" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
        </div>
        {kind === "estimate" ? (
          <div>
            <Label>Type</Label>
            <Select value={typeId} onChange={(e) => pickType(e.target.value)}>
              <option value="">{settings.estimateTypes.length ? "No type" : "No types yet (Types & terms)"}</option>
              {settings.estimateTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
        <div className={kind === "invoice" ? "col-span-2 sm:col-span-1" : ""}>
          <Label>{kind === "estimate" ? "Estimate owner (who sold it)" : "Sold by"}</Label>
          <Select value={salespersonUid} onChange={(e) => setSalespersonUid(e.target.value)}>
            <option value="">Nobody</option>
            {team.map((m) => (
              <option key={m.uid} value={m.uid}>
                {m.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-steel">
            <th className="pb-1 font-medium">Description</th>
            <th className="w-16 pb-1 font-medium">Qty</th>
            <th className="w-24 pb-1 font-medium">Price</th>
            <th className="w-12 pb-1 text-center font-medium">Tax</th>
            <th className="w-20 pb-1 text-right font-medium">Amount</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.key}>
              <td className="pr-2 pb-1.5">
                <Input value={l.description} onChange={(e) => setLine(l.key, { description: e.target.value })} placeholder="What's being done" />
                {l.components && l.components.length > 0 ? (
                  <div className="mt-0.5 pl-2 text-[11px] leading-tight text-steel">
                    {l.components.map((c, i) => (
                      <div key={i}>
                        • {c.quantity !== 1 ? `${c.quantity} × ` : ""}
                        {c.name}
                      </div>
                    ))}
                    <label className="mt-0.5 flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={l.showComponents}
                        onChange={(e) => setLine(l.key, { showComponents: e.target.checked })}
                      />
                      show on customer doc
                    </label>
                  </div>
                ) : null}
              </td>
              <td className="pr-2 pb-1.5">
                <Input
                  type="number"
                  min="0.25"
                  step="0.25"
                  value={l.quantity}
                  onChange={(e) => setLine(l.key, { quantity: parseFloat(e.target.value) || 0 })}
                />
              </td>
              <td className="pr-2 pb-1.5">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={(l.unitPriceCents / 100).toString()}
                  onChange={(e) => setLine(l.key, { unitPriceCents: Math.round(parseFloat(e.target.value || "0") * 100) })}
                />
              </td>
              <td className="pb-1.5 text-center">
                <input type="checkbox" checked={l.taxable} onChange={(e) => setLine(l.key, { taxable: e.target.checked })} />
              </td>
              <td className={cnAmt(l.priceVisibility)}>{money(Math.round(l.quantity * l.unitPriceCents))}</td>
              <td className="pb-1.5 text-right">
                <span className="inline-flex items-center gap-1.5">
                  <button
                    className={l.priceVisibility === "hide" ? "text-brandorange-dark" : "text-steel/50 hover:text-ink"}
                    onClick={() => setLine(l.key, { priceVisibility: l.priceVisibility === "hide" ? "show" : "hide" })}
                    title={l.priceVisibility === "hide" ? "Price hidden on customer doc — click to show" : "Hide price on customer doc"}
                    aria-label="Toggle price visibility"
                  >
                    {l.priceVisibility === "hide" ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button
                    className="text-steel hover:text-danger"
                    onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                    aria-label="Remove line"
                  >
                    <Trash2 size={14} />
                  </button>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-1 flex gap-2">
        <Button
          variant="ghost"
          className="px-2 py-1 text-xs"
          onClick={() => setLines((prev) => [...prev, blankLine(`n${Date.now()}`)])}
        >
          <Plus size={14} /> Blank line
        </Button>
        {pricebook.length > 0 ? (
          <div className="relative">
            <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setPickerOpen((v) => !v)}>
              <Plus size={14} /> From pricebook
            </Button>
            {pickerOpen ? (
              <div className="absolute z-20 mt-1 w-72 rounded-lg border border-steel/30 bg-white p-2 shadow-lg">
                <Input
                  autoFocus
                  placeholder="Search pricebook…"
                  className="mb-1"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                />
                <div className="max-h-56 overflow-y-auto">
                  {filteredPricebook.map((p) => (
                    <button
                      key={p.id}
                      className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs hover:bg-cream"
                      onClick={() => addFromPricebook(p)}
                    >
                      <span className="flex min-w-0 items-center gap-1.5 truncate text-ink">
                        {p.photoUrl ? <img src={p.photoUrl} alt="" className="h-6 w-6 shrink-0 rounded object-cover" loading="lazy" /> : null}
                        {p.name}
                        {p.kind === "kit" ? (
                          <span className="ml-1.5 rounded-full bg-brandorange/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-brandorange-dark">
                            kit
                          </span>
                        ) : null}
                      </span>
                      <span className="ml-2 shrink-0 text-steel">{money(p.priceCents)}</span>
                    </button>
                  ))}
                  {filteredPricebook.length === 0 ? (
                    <p className="px-2 py-2 text-xs text-steel">No matches.</p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="mt-3 flex items-end justify-between gap-3 border-t border-steel/15 pt-3">
        <div className="flex-1">
          <Label>Notes (appears on the document)</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          {walks.length ? (
            <div className="mt-1 flex flex-wrap gap-2 text-xs">
              {walks.map((w) => (
                <button key={w.jobId} type="button" className="text-brandorange-dark underline" onClick={() => setNotes(walkNotes(w))}>
                  Use the job walk's notes ({w.title}, {new Date(w.doneAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })})
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <div className="text-right text-sm">
          {planDiscount ? (
            <div className="text-steel">
              {planDiscount.name} member discount ({(planDiscount.bps / 100).toString().replace(/\.0$/, "")}%){" "}
              {mdMode === "auto" ? <span className="text-ink">−{money(discountCents)}</span> : <span>not applied</span>}{" "}
              <button type="button" className="text-xs text-brandorange-dark hover:text-brandorange" onClick={() => setMdMode((m) => (m === "auto" ? "off" : "auto"))}>
                {mdMode === "auto" ? "take off this one" : "apply"}
              </button>
            </div>
          ) : null}
          <div className="text-steel">Subtotal {money(totals.subtotalCents)}</div>
          <div className="text-steel">Tax {money(totals.taxCents)}</div>
          <div className="text-base font-bold text-ink">Total {money(totals.totalCents)}</div>
        </div>
      </div>
      {kind === "estimate" ? (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-2 text-sm">
          <span className="text-steel">Deposit when signed</span>
          <DepositInput compact value={deposit} onChange={setDeposit} />
          {deposit && depositCents(deposit, totals.totalCents) > 0 ? <span className="font-semibold text-ink">{money(depositCents(deposit, totals.totalCents))}</span> : null}
        </div>
      ) : null}
      <div className="mt-3">
        <button type="button" className="flex items-center gap-1 text-xs font-semibold text-steel hover:text-ink" onClick={() => setShowTerms((v) => !v)}>
          {showTerms ? "▾" : "▸"} Terms {terms.trim() ? `(${terms.trim().split(/\s+/).length} words)` : "(none)"}
        </button>
        {showTerms ? (
          <textarea
            className={`${textareaClass} mt-1`}
            rows={6}
            maxLength={20000}
            value={terms}
            placeholder={kind === "estimate" ? "Pick a type to start from its terms, or write them here." : "Payment terms, late fees…"}
            onChange={(e) => {
              setTerms(e.target.value);
              setTermsTouched(true);
            }}
          />
        ) : null}
      </div>
      {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button onClick={() => void save()} busy={busy}>
          Save {kind}
        </Button>
      </div>
    </Card>
  );
}
